import React, { useMemo } from "react";
import { useUserContext } from "../contexts/UserContext";
import { resolveCurrentPlan } from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";

const PRICING_URL = "https://ec-vision.co.jp/rieki-navi/#plan";

function normalizePlanLabel(plan: string): string {
  const p = String(plan || "").trim();
  if (!p) return "未設定";
  if (p === "free" || p.toLowerCase() === "free") return "フリープラン";
  return p;
}

function pillClass(plan: string): string {
  const p = String(plan || "").toLowerCase();
  if (p.includes("free") || p.includes("フリー")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (p.includes("trial") || p.includes("トライアル")) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  return "bg-indigo-50 text-indigo-700 border-indigo-200";
}

export default function PlanBillingPage() {
  const user = useUserContext();

  const currentPlan = useMemo(() => {
    const resolved = resolveCurrentPlan(user);
    return normalizePlanLabel(resolved);
  }, [user]);

  const currentFeatures = [
    "現在プランで利用可能な主要機能",
    "日次・月次・期間の利益確認",
    "店舗運営に必要な基本設定",
  ];

  const upgradeFeatures = [
    "月次分析レポート（全40ページ）",
    "個別チャットサポート",
    "含められる店舗数の増加",
  ];

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-slate-800">プラン・お支払い</h1>
        <p className="mt-2 text-sm text-slate-600">
          現在の契約プランを確認し、プラン変更を申請できます。
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-600">現在のプラン</span>
          <span
            className={[
              "inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold",
              pillClass(currentPlan),
            ].join(" ")}
          >
            {currentPlan}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-800">現在プランに含まれる内容</h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            {currentFeatures.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <span className="mt-[2px] h-2 w-2 shrink-0 rounded-full bg-slate-400" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-800">上位プランで追加される内容</h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            {upgradeFeatures.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <span className="mt-[2px] h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => openUpgradeForm("プラン・請求ページ")}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            プラン変更を申請する
          </button>

          <button
            type="button"
            onClick={() => window.open(PRICING_URL, "_blank", "noopener,noreferrer")}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            料金表を見る
          </button>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          申請内容を確認後、運営側で契約変更手続きを行います。
        </p>
      </div>
    </div>
  );
}
